import { Component, Event, EventEmitter, getAssetPath, h, Host, Prop, State } from '@stencil/core';

import wcShared from '@web-components/shared';

import { SupportPremiumLevelId } from '../../../../utils/enum';
import helpdeskCommon from '../../../../utils/HelpdeskCommon.class';
import { InitStore } from '../../../../utils/Stores/init-store';
import { Store } from '../../../../utils/Stores/store';
import { getPhoneWaitingTime } from '../../../../utils/utils';

@Component({
  tag: 'contact-phone-module',
  styleUrl: 'contact-phone-module.scss',
  shadow: true,
})
export class ModuleContactPhone {
  @Event() getInitEvent: EventEmitter;
  @State() progress = 0;
  @State() telFormated: string = null;
  @Prop() readonly availability: IWCAvailability;
  @Prop() readonly phoneEnabled: boolean;
  @Event() sectionChangeEvent: EventEmitter<IWCContextClose[]>;

  private phoneWaitingTime = getPhoneWaitingTime();
  private isCallmebackQuotaOver: boolean =
    InitStore?.get('init')?.phone['cmb_settings']?.user_quota_reached;
  private isCallmebackPremiumOnly: boolean =
    InitStore?.get('init')?.phone['cmb_settings']?.enabled_for === 'support_premium';
  private isUserPremium = [
    SupportPremiumLevelId.plus,
    SupportPremiumLevelId.pro,
    SupportPremiumLevelId.business,
  ].includes(Store?.get('supportPremium').supportLevel);
  private isCallmebackEnabled: boolean =
    InitStore?.get('init')?.phone['cmb_settings']?.is_enabled &&
    (this.isCallmebackPremiumOnly ? this.isUserPremium : true) &&
    !this.isCallmebackQuotaOver &&
    Boolean(InitStore?.get('init')?.user);
  private isCallmebackHighlighted: boolean = this.isCallmebackEnabled && this.phoneWaitingTime > 5;

  private loadingAccess = {
    workspace: false,
    kdrive: false,
  };
  private color = '#0098FF';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private codeInterval: any;
  private maxWaitingTime = 180;

  componentWillLoad() {
    this.generateProgress();
  }

  componentDidLoad() {
    // POST Statistic
    wcShared?.httpCall('PUT', '/api/components/chatbot/contact-type', { contact_type: 'phone' });

    this.telFormated = InitStore?.get('init')?.phone?.int?.replace(/\s/g, '');
    this.telFormated = this.telFormated.replace('(0)', '');
  }

  private getGraphData(): number[] {
    const phoneWaitingValues = InitStore?.get('init').phone.phone_waiting_values;
    // If VIP shcedule is sent it means that the user is VIP and thus need to see the VIP schedule
    if (phoneWaitingValues?.vip) return phoneWaitingValues.vip.slice(5, 17);
    const contactType = Store?.get('messageData').contact_type;
    // If contactType hasn't a linked schedule use the default one
    if (
      !phoneWaitingValues ||
      !contactType ||
      !Object.keys(phoneWaitingValues).includes(contactType)
    )
      return [20, 30, 70, 100, 60, 20, 40, 35, 55, 44, 35, 20];
    return phoneWaitingValues[contactType].slice(5, 17);
  }

  private getTooltipCause(): string {
    if (this.isCallmebackPremiumOnly && !this.isUserPremium) {
      return wcShared?.translate('Souscrivez au Support Premium pour bénéficier de cette option');
    }
    if (this.isCallmebackQuotaOver) {
      return wcShared?.translate(
        'Deux planifications d’appel maximum au support par jour. Si votre problème persiste veuillez appeler le support maintenant',
      );
    }
    if (!Boolean(InitStore?.get('init')?.user) && !this.isCallmebackPremiumOnly) {
      return wcShared?.translate('Veuillez vous connecter pour demander à être rappelé');
    }
    return wcShared?.translate('Aucun créneau disponible, veuillez appeler le support maintenant');
  }

  private goToSection(section: IWCContextClose) {
    this.sectionChangeEvent.emit([section]);
  }

  private generateProgress() {
    if (!Boolean(InitStore?.get('init')?.code?.next_refresh)) {
      return;
    }

    let chronometre = this.maxWaitingTime - InitStore?.get('init')?.code?.next_refresh;

    this.codeInterval = setInterval(() => {
      if (chronometre < this.maxWaitingTime) {
        chronometre++;
      } else {
        chronometre = 0;
        helpdeskCommon?.getInit();
      }
      this.progress = chronometre;
    }, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.codeInterval);
  }

  private authorizationChange(event, name) {
    if (this.loadingAccess[name]) {
      return;
    }

    this.loadingAccess[name] = true;

    if (event.target.checked) {
      wcShared
        .httpCall('POST', `/api/components/chatbot/support/${name}/access`)
        .then()
        .then(() => {
          this.loadingAccess[name] = false;
        });
    } else {
      wcShared
        .httpCall('DELETE', `/api/components/chatbot/support/${name}/access`)
        .then()
        .then(() => {
          this.loadingAccess[name] = false;
        });
    }
  }

  render() {
    return (
      <Host>
        <div class={{ phone2: true, 'phone2--full': Store?.get('is_full') }}>
          <div class="phone2__container">
            <div class="support-ik">
              <img
                src={getAssetPath('./../assets/rounded-icons/icon-headset-white-bg.svg')}
                alt="Support Infomaniak"
              />
              <span>{wcShared?.translate('Support infomaniak')}</span>
            </div>

            <div class="phone-block">
              <div class="phone-block__title">
                <div>{wcShared?.translate('Appel prioritaire')}</div>
                <div class={{ 'central-state': true, disabled: !this.phoneEnabled }}>
                  <span class="dot"></span>
                  {this.phoneEnabled
                    ? wcShared?.translate('Centrale ouverte')
                    : wcShared?.translate('Centrale fermee')}
                </div>
              </div>

              <div class="phone-block__opening-time">
                <div>
                  <img
                    src={getAssetPath('./../assets/icons/calendar.svg')}
                    alt="Calendar"
                  />
                  {wcShared?.translate('$count$ j. / 7', { count: this.availability?.days })}
                </div>
                <div>
                  <img
                    src={getAssetPath('./../assets/icons/time-clock-circle.svg')}
                    alt="Time clock"
                  />
                  {wcShared?.translate('$fromHour$ a $toHour$', {
                    fromHour: this.availability?.hour.from,
                    toHour: this.availability?.hour.to,
                  })}
                </div>
              </div>

              <div class="phone-block__stats-block">
                <div class={{ header: true, hide: !this.phoneEnabled }}>
                  <div class="header__awaiting-time">
                    {wcShared?.translate("Temps d'attente estime")}
                  </div>
                  <div class="header__timer">
                    {this.phoneWaitingTime}{' '}
                    <span>
                      {this.phoneWaitingTime > 1
                        ? wcShared?.translate('minutes')
                        : wcShared?.translate('minute')}
                    </span>
                  </div>
                  {Store?.get('currentGroup')?.legal_entity_type === 'company' &&
                    Store?.get('supportPremium').supportLevel ===
                      SupportPremiumLevelId.included && (
                      <div class="header__support-premium-ad">
                        <support-premium-ad-component></support-premium-ad-component>
                      </div>
                    )}
                </div>
                <div class={{ header: true, hide: this.phoneEnabled }}>
                  <div
                    class="header__close-text"
                    innerHTML={wcShared?.translate(
                      'La centrale telephonique est actuellement <strong>Fermee</strong>',
                    )}
                  ></div>
                </div>
                <phone-stats-module
                  data={{
                    columns: this.getGraphData(),
                    timeline: [
                      '6:00',
                      '',
                      '',
                      '9:00',
                      '',
                      '',
                      '12:00',
                      '',
                      '',
                      '15:00',
                      '',
                      '17:00',
                    ],
                  }}
                  color_bg={this.color}
                ></phone-stats-module>
              </div>

              <div class="phone-block__support-contact">
                <div class="contact-line">
                  {wcShared?.translate('Contacter le support')}{' '}
                  <span>
                    (
                    {Boolean(InitStore?.get('init')?.phone?.local)
                      ? wcShared?.translate("Prix d'un appel local")
                      : wcShared?.translate('tarif internationnal')}
                    )
                  </span>
                </div>
                <div
                  class={{
                    'contact-info': true,
                    'not-vip': Store?.get('supportPremium').vipCode === null,
                    highlight: this.isCallmebackHighlighted,
                  }}
                >
                  <div class="container">
                    <div class="number">
                      {InitStore?.get('init')?.phone?.local ?? InitStore?.get('init')?.phone?.int}
                    </div>
                    <div class="vip-code">
                      {wcShared?.translate('Code VIP')}{' '}
                      <span>{Store?.get('supportPremium').vipCode}</span>
                      <div class="tooltip">
                        <img
                          src={getAssetPath('./../assets/icons/information-circle.svg')}
                          alt={wcShared.translate('Icon information')}
                        />
                        <span class="tooltiptext">
                          {wcShared.translate(
                            'Ce code permet a notre support de valider votre niveau de support lors de vos appels telephoniques.',
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="phone-block__call-action">
                    <a
                      href={'tel:' + this.telFormated}
                      class={{ disabled: !this.phoneEnabled }}
                    >
                      <button
                        class={{ 'not-highlight': this.isCallmebackHighlighted }}
                        type="button"
                        disabled={!this.phoneEnabled}
                      >
                        {wcShared?.translate('Appeler maintenant')}
                      </button>
                    </a>
                  </div>
                </div>
                <button
                  onClick={
                    this.isCallmebackEnabled ? () => this.goToSection('contact_callmeback') : null
                  }
                  class={{
                    callmeback: true,
                    highlight: this.isCallmebackHighlighted,
                    'disable tooltip': !this.isCallmebackEnabled,
                  }}
                >
                  {!this.isCallmebackEnabled && (
                    <span class="tooltiptext">{this.getTooltipCause()}</span>
                  )}

                  {wcShared?.translate('Demander à être rappelé')}
                </button>
              </div>
            </div>

            <div class={{ 'phone-block': true, hide: !Boolean(InitStore?.get('init')?.user?.id) }}>
              <div class="phone-block__code-authorization">
                <div class="code-line">{wcShared?.translate("Code d'autorisation")}</div>
                <div class="code-info">
                  <div>{InitStore?.get('init')?.code?.code}</div>
                  <module-progress-spinner-component
                    stroke={2}
                    radius={14}
                    progress={this.progress}
                  ></module-progress-spinner-component>
                </div>
              </div>

              <div class="phone-block__access-authorization">
                <div class="access-line">
                  {wcShared?.translate("Autorisation d'acces pour le support")}
                </div>
                <div class="access-choice">
                  <div class="choices">
                    <div class="choice__text">{wcShared?.translate('Mail, Calendar, Contact')}</div>
                    <div class="choice__switch">
                      <input
                        type="checkbox"
                        id="workspace"
                        checked={InitStore?.get('init')?.support?.workspace_access}
                        onChange={(event) => this.authorizationChange(event, 'workspace')}
                      />
                      <label htmlFor="workspace"></label>
                    </div>
                  </div>
                  {InitStore?.get('init')?.support?.drive_access !== null && (
                    <div class="choices">
                      <div class="choice__text">{wcShared?.translate('kDrive')}</div>
                      <div class="choice__switch">
                        <input
                          type="checkbox"
                          checked={InitStore?.get('init')?.support?.drive_access}
                          onChange={(event) => this.authorizationChange(event, 'kdrive')}
                          id="kdrive"
                        />
                        <label htmlFor="kdrive"></label>
                      </div>
                    </div>
                  )}
                  {InitStore?.get('init')?.support?.kchat_access !== null && (
                    <div class="choices">
                      <div class="choice__text">{wcShared?.translate('kChat')}</div>
                      <div class="choice__switch">
                        <input
                          type="checkbox"
                          checked={InitStore?.get('init')?.support?.kchat_access}
                          onChange={(event) => this.authorizationChange(event, 'kchat')}
                          id="kchat"
                        />
                        <label htmlFor="kchat"></label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
